<template>
<ValidationObserver v-slot="{ invalid, handleSubmit }" ref="observer">
    <form class="season-form" @submit.prevent="handleSubmit(saveSeason)" novalidate>
        <div class="season__wrapper edit-box box-shadow" :class="{ 'is-locked' : !isEditMode, 'is-lock': seasonCopy.is_lock }" :style="{ 'border-bottom': '2px solid '+seasonCopy.color }">
            <div class="season__wrapper--inner">
                <div class="columns">
                    <div class="column">
                        <div class="edit-box__header">
                            <div class="is-clearfix">
                                <div class="edit-box__headline-wrapper is-pulled-left">
                                    <h3 class="edit-box__headline" v-if="!isEditMode"> {{ seasonCopy.name}} </h3>
                                    <ValidationProvider v-if="isEditMode" v-slot="{ errors }" rules="required">
                                        <input type="text" placeholder="Name" class="edit-box--input input"
                                            @click="fromFieldClicked('name')"
                                            :class="{'is-danger': errors[0] }" name="seasonCopy.name" v-model="seasonCopy.name">
                                        <span v-show="errors[0]" class="help is-danger">{{ errors[0] }}</span>
                                    </ValidationProvider>
                                </div>
                                <div class="edit-box__toggle-edit is-pulled-right" @click="toggleEditMode()">
                                    <a class="button" v-show="!isEditMode"><i class="fa fa-pencil-square-o fa-lg" aria-hidden="true"></i></a>
                                    <a class="button" v-show="isEditMode"><i class="fa fa-times fa-lg" aria-hidden="true"></i></a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="edit-box__section" v-if="isEditMode">
                    <label class="checkbox">
                        <vb-switch class="season__lock" size="small" :disabled="!isEditMode"
                                   :checked="seasonCopy.is_lock" v-model="seasonCopy.is_lock" @click.native="fromFieldClicked('is_lock')"></vb-switch>
                        <span class="season__sperrzeit__label is-unselectable" :class="{'is-disabled': seasonCopy.is_lock}">{{ $t('seasons.close_time') }}</span>
                    </label>
                </div>

                <div class="edit-box__section">
                    <label class="label season__color__label">{{ $t('seasons.color') }}</label>
                    <span class="season__color__preview" :class="{'is-editable' : isEditMode}"  :style="{ 'background-color': seasonCopy.color }"></span>
                    <ul class="season__color__list">
                        <li class="season__color__list-item" :style="{ 'background-color': color }"
                            v-if="isEditMode" @click="onColorChanged(color)" v-for="color in defaultColors">
                            <div class="season__color__list-item--active" v-show="color === seasonCopy.color"></div>
                        </li>
                    </ul>
                </div>

                <div class="edit-box__section is-clearfix">
                    <label class="label">{{ $t('seasons.periods') }}</label>

                    <div class="hidden-time-periods" :class="{'active': showOldPeriods}" v-if="isEditMode && hasTimePeriodsInPast">
                        <a @click.prevent="toggleOldPeriods(true)" class="show-link">{{ $t('seasons.show_old_periods') }}</a>
                        <div :key="index" class="time-period-edit-wrapper" v-for="(period, index) in seasonCopy.time_periods" v-if="timePeriodIsInPast(period)">
                            <ValidationProvider data-vv-name="time_period" data-vv-value-path="seasonCopy.time_periods[index]"  v-slot="{ errors }" rules="time_period">
                                <time-period
                                v-model="seasonCopy.time_periods[index]"
                                @del="removeTimePeriod(index)"
                                :error="apiErrors.time_periods"
                                :deleteable="isDeletable"
                                data-vv-value-path="seasonCopy.time_periods[index]"
                                data-vv-name="time_period">
                                </time-period>
                                <span v-show="errors[0]" class="help is-danger">{{ errors[0] }}</span>
                            </ValidationProvider>
                        </div>
                        <a @click.prevent="toggleOldPeriods(false)" class="hide-link">{{ $t('seasons.hide_old_periods') }}</a>
                    </div>

                    <div v-if="isEditMode">
                        <div :key="index" v-for="(period, index) in seasonCopy.time_periods" v-if="!timePeriodIsInPast(period)">
                            <ValidationProvider data-vv-value-path="seasonCopy.time_periods[index]"  data-vv-name="time_period" v-slot="{ errors }" rules="time_period">
                                <time-period
                                v-model="seasonCopy.time_periods[index]"
                                @del="removeTimePeriod(index)"
                                :error="apiErrors.time_periods" :deleteable="isDeletable"
                                data-vv-value-path="seasonCopy.time_periods[index]"
                                data-vv-name="time_period">
                                </time-period>
                                <span v-show="errors[0]" class="help is-danger">{{ errors[0] }}</span>
                            </ValidationProvider>
                        </div>
                        <div class="season__time--add">
                            <a class="button is-pulled-right" @click="addTimePeriod()">
                                <i class="fa fa-plus" aria-hidden="true"></i>
                                <span>{{ $t('seasons.new_period') }}</span>
                            </a>
                        </div>
                    </div>

                    <div class="hidden-time-periods" :class="{'active': showOldPeriods}" v-if="!isEditMode && hasTimePeriodsInPast">
                        <a @click.prevent="toggleOldPeriods(true)" class="show-link">{{ $t('seasons.show_old_periods') }}</a>
                        <div class="season-time-wrapper" v-for="(period, index) in seasonCopy.time_periods" v-if="timePeriodIsInPast(period)">
                            <span class="season-time-start">{{ period.date_start | localizedDate('L') }}</span>
                            <span class="season-time-spacer">{{ $t('seasons.until') }}</span>
                            <span class="season-time-start">{{ period.date_end | localizedDate('L') }}</span>
                        </div>
                        <a @click.prevent="toggleOldPeriods(false)" class="hide-link">{{ $t('seasons.hide_old_periods') }}</a>
                    </div>

                    <div v-if="!isEditMode">
                        <div class="season-time-wrapper" v-for="(period, index) in seasonCopy.time_periods" v-if="!timePeriodIsInPast(period)">
                            <span class="season-time-start">{{ period.date_start | localizedDate('L') }}</span>
                            <span class="season-time-spacer">{{ $t('seasons.until') }}</span>
                            <span class="season-time-start">{{ period.date_end | localizedDate('L') }}</span>
                        </div>
                    </div>
                </div>

                <div class="edit-box__section minimum-nights is-clearfix" v-if="!seasonCopy.is_lock">
                    <h4>{{ $t('seasons.minimumNights') }}</h4>
                    <div v-if="isEditMode">
                        <div class="columns" :key="`${ctype}_${idx}`" v-for="(ctypeName, ctype, idx) in campsiteTypes">
                            <div class="column is-4 label-column">
                                <label class="label">{{ ctypeName }}:</label>
                            </div>
                            <div class="column is-8">
                                <ValidationProvider v-slot="{ errors }" rules="required" :key="`${ctype}_${idx}`">
                                    <input
                                    type="number"
                                    min="0" max="999"
                                    class="edit-box--input input edit-box__inline-input"
                                    v-if="isEditMode"
                                    @click="fromFieldClicked('name')"
                                    :class="{'is-danger': !!minimumNightsErrors.length }" v-model="seasonCopy.minimum_nights[getMinimumNightsIndex(ctype)].amount">
                                    <span class="nights">{{ $tc('seasons.nights', seasonCopy.minimum_nights[getMinimumNightsIndex(ctype)].amount) }}</span>
                                    <span v-if="false">{{errors}}</span>
                                </ValidationProvider>
                            </div>
                        </div>

                        <div v-if="minimumNightsErrors.length" class="help is-danger">
                            <div v-for="err of minimumNightsErrors">
                                {{ err }}
                            </div>
                        </div>
                    </div>
                    <div v-if="!isEditMode">
                        <div :key="`${ctype}_${index}`" class="season-ctype-wrapper" v-for="(ctypeName, ctype, index) in campsiteTypes">
                            <div class="columns">
                                <div class="column is-4">
                                    <strong>{{ ctypeName }}: </strong>
                                </div>
                                <div class="column is-8">
                                    {{ seasonCopy.minimum_nights[getMinimumNightsIndex(ctype)].amount }} {{ $tc('seasons.nights', seasonCopy.minimum_nights[getMinimumNightsIndex(ctype)].amount) }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>


                <!-- backend errors -->
                <div class="error-messages" v-if="isEditMode">
                    <span class="help is-danger" v-for="(value, key) in apiErrors">
                        <span v-for="msg in value" v-html="msg"></span>
                    </span>
                </div>
                <div class="edit-box__footer is-clearfix" v-if="isEditMode">
                    <a class="button is-link is-pulled-left" v-if="seasonCopy.id" @click="deleteSeason()">
                        <span class="icon">
                            <i class="fa fa-trash" aria-hidden="true"></i>
                        </span>
                        <span>{{ $t('seasons.delete') }}</span>
                    </a>
                    <button class="button is-primary is-pulled-right"
                       :class="{ 'is-loading': isLoading, 'is-disabled': invalid }">
                        {{ $t('generic.save') }}
                    </button>
                    <a class="button is-link is-pulled-right" @click="toggleEditMode()">{{ $t('generic.cancel') }}</a>
                </div>
            </div>
            <div class="edit-box__dragger" v-if="!isEditMode">
                <div class="handle">
                    <span></span>
                    <span></span>
                    <span></span>
                </div>
            </div>
        </div>
    </form>
</ValidationObserver>
</template>

<script>
    import moment from 'moment';
    import VbSwitch from 'vue-bulma-switch';
    import { cloneDeep } from 'lodash';

    import filters from '../../../filters';
    import { Api } from '../../../api';
    import { AppModel } from '../../../models';
    import { EventBus, Tracking } from '../../../utils';
    import { TimePeriod } from '../';

    const defaultColors = [
        '#2E2E2E',
        '#4B515D',
        '#ff4444',
        '#ffbb33',
        '#00C851',
        '#2BBBAD',
        '#33b5e5',
        '#4285F4',
        '#aa66cc',
    ];

    export default {
        name: 'season',

        props: {
            value: {
                type: Object,
                'default': () => ([]),
            },
        },

        components: {
            TimePeriod,
            VbSwitch,
        },

        filters: filters,

        created() {
            // copy original data to object
            this.seasonCopy = cloneDeep(this.value);
            this.campsiteTypes = AppModel.campsiteTypes;
        },

        data() {
            return {
                defaultColors: defaultColors,
                seasonCopy: null,
                isEditMode: !this.value.id, // if seasonCopyCopyCopy has no id, display it in edit mode
                colorChooserVisible: false,
                color: {
                    hex: this.value.color
                },
                isLoading: false,
                haveChanged: false,
                apiErrors: {},
                isDeletable: true,
                campsiteTypes: [],
                minimumNightsErrors: [],
                showOldPeriods: false,
            };
        },

        watch: {
            isEditMode(newValue, oldValue) {
                if(newValue === false) {
                    this.apiErrors = {};
                    this.minimumNightsErrors = [];
                }
            },

            'seasonCopy.time_periods': function(newValue) {
                this.isDeletable = newValue.length > 1;
            }
        },

        validator: null,

        computed: {
            hasTimePeriodsInPast() {
                for(let period of this.seasonCopy.time_periods) {
                    if(this.timePeriodIsInPast(period)) {
                        return true;
                    }
                }
                return false;
            }
        },

        methods: {
            saveSeason() {
                this.$refs.observer.validate().then(result => {
                    if(this.seasonCopy.id) {
                        this.updateSeason();
                    } else {
                        this.addSeason();
                    }
                }).catch(result => {
                    Tracking.trackEvent('Season', 'updateSeason', 'formInvalid', null);
                });
            },

            addSeason() {
                this.onUpdateStart();
                this.seasonCopy.campground_slug = AppModel.activeCampground.slug;
                Api.seasons.addSeason(this.seasonCopy)
                    .then((response) => {
                        if(response.ok) {
                            this.seasonCopy.id = response.body.id;
                            this.onUpdateDone();
                        } else {
                            this.onUpdateFailed(response);
                        }
                    }, () => {
                        this.onUpdateFailed();
                    });
                Tracking.trackEvent('Season', 'saveSeason', null, null);
            },

            updateSeason() {
                this.onUpdateStart();
                this.seasonCopy.campground_slug = AppModel.activeCampground.slug;
                Api.seasons.updateSeason(this.seasonCopy)
                    .then((response) => {
                        if(response.ok) {
                            this.onUpdateDone();
                        } else {
                            this.onUpdateFailed(response);
                        }
                    }, () => {
                        this.onUpdateFailed();
                    });
                Tracking.trackEvent('Season', 'updateSeason', null, null);
            },

            deleteSeason() {
                this.$emit('del');
            },

            toggleEditMode() {
                this.isEditMode = !this.isEditMode;
                this.showOldPeriods = false;
                // if editor leaves edit mode remove unsaved season
                // or restore initial data
                if(!this.isEditMode) {
                    if(!this.seasonCopy.id) {
                        this.deleteSeason();
                    } else {
                        this.seasonCopy = cloneDeep(this.value);
                    }
                }
                Tracking.trackEvent('Season', 'toggleEditMode', null, null);
            },

            onUpdateStart() {
                this.isLoading = true;
            },

            onUpdateDone() {
                this.$emit('input', this.seasonCopy);
                this.isLoading = false;
                this.isEditMode = false;
                this.showOldPeriods = false;
                EventBus.$emit(EventBus.UPDATE_CHECKS);
                EventBus.$emit(EventBus.SHOW_NOTIFICATION, { title: `<h3>${this.$t('generic.saveSuccessHeadline')}</h3>`, direction: 'Up', body: this.$t('generic.saveSuccess') });
                Tracking.trackEvent('Season', 'updateSeason', 'success', null);
            },

            onUpdateFailed(response = null) {
                if(response) {
                    if(typeof response.body !== 'string') {
                        this.apiErrors = response.body;
                        if(this.apiErrors['minimum_nights']) {
                            this.minimumNightsErrors = {};
                            for(let e of this.apiErrors['minimum_nights']) {
                                const vals = Object.values(e);
                                if(vals && vals.length) {
                                    Object.keys(e).forEach((key, idx) => {
                                        this.minimumNightsErrors[key] = e[key][0];
                                    });
                                }
                            }
                            delete this.apiErrors['minimum_nights'];
                            this.minimumNightsErrors = Object.values(this.minimumNightsErrors);
                        }
                    } else {
                        EventBus.$emit(EventBus.SHOW_NOTIFICATION, { title: `<h3>${this.$t('errors.500.title')}</h3>`, direction: 'Up', type: 'danger', body: this.$t('errors.500.content') });
                    }
                } else {
                    EventBus.$emit(EventBus.SHOW_NOTIFICATION, { title: `<h3>${this.$t('generic.formErrorHeadline')}</h3>`, direction: 'Up', type: 'danger', body: this.$t('generic.formError') });
                }
                this.isLoading = false;
                Tracking.trackEvent('Season', 'updateSeason', 'failed', null);
            },

            addTimePeriod() {
                let period = {
                    date_start: moment().format('YYYY-MM-DD'),
                    date_end: moment().format('YYYY-MM-DD')
                };
                this.seasonCopy.time_periods.push(period);
                Tracking.trackEvent('Season', 'addTimePeriod', null, null);
            },

            removeTimePeriod(idx) {
                // remove one object at specified index
                this.seasonCopy.time_periods.splice(idx, 1);
                Tracking.trackEvent('Season', 'removeTimePeriod', null, null);
            },

            onColorChanged(color) {
                this.seasonCopy.color = color;
                this.colorChooserVisible = false;
                Tracking.trackEvent('Season', 'changeColor', null, null);
            },

            fromFieldClicked(fieldName) {
                Tracking.trackEvent('Season', 'formField', fieldName, null);
            },

            getMinimumNightsIndex(ctype) {
                for(let idx in this.seasonCopy.minimum_nights) {
                    if(this.seasonCopy.minimum_nights[idx].campsite_type === ctype) {
                        return idx;
                    }
                }
                return -1;
            },

            timePeriodIsInPast(period) {
                return moment().startOf('day').isAfter(moment(period.date_end));
            },

            toggleOldPeriods(show) {
                this.showOldPeriods = show;
            },
        },
    };
</script>

<style lang="scss">
    @import 'season.scss';
</style>



// WEBPACK FOOTER //
// src/views/components/season/season.vue